import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Banknote, Pencil } from 'lucide-react'
import { useDriver, useActivateDriver, useDeactivateDriver } from '@/hooks/useDrivers'
import { StatusBadge } from '@/components/ui/status-badge'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Button } from '@/components/ui/button'
import { ExpiryBanner } from '@/components/ui/expiry-banner'
import { maskCpf, maskPhone } from '@/components/form/masks'

const DRIVER_TYPE_LABEL: Record<string, string> = {
  OWN: 'Próprio',
  AGGREGATE: 'Agregado',
}

const BANK_TYPE_LABEL: Record<string, string> = {
  corrente: 'Corrente',
  poupanca: 'Poupança',
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' })
}

function DetailRow({ label, value }: Readonly<{ label: string; value?: string | null }>) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || '—'}</dd>
    </div>
  )
}

export function DriverDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { data: driver, isLoading } = useDriver(id ?? '')
  const activate = useActivateDriver()
  const deactivate = useDeactivateDriver()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const isPending = activate.isPending || deactivate.isPending

  async function handleToggle() {
    if (!driver) return
    try {
      if (driver.active) {
        await deactivate.mutateAsync(driver.id)
        toast.success('Motorista desativado')
      } else {
        await activate.mutateAsync(driver.id)
        toast.success('Motorista ativado')
      }
    } catch {
      toast.error('Erro ao alterar status do motorista')
    } finally {
      setConfirmOpen(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  if (!driver) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Motorista não encontrado.</p>
      </div>
    )
  }

  const hasCnh = driver.cnhNumber || driver.cnhCategory.length > 0 || driver.cnhExpiry
  const hasAddress = driver.city || driver.state || driver.street
  const hasBank = driver.bankName || driver.bankAgency || driver.bankAccount || driver.pixKey

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate('/drivers')}>
          <ArrowLeft size={16} />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-xl font-semibold text-foreground truncate">{driver.name}</h1>
            <StatusBadge active={driver.active} />
          </div>
          <p className="text-sm text-muted-foreground truncate">
            {DRIVER_TYPE_LABEL[driver.driverType] ?? driver.driverType}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/repayments/consolidated?driverId=${driver.id}`)}
        >
          <Banknote size={14} />
          Ver repasses
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => navigate(`/drivers/${driver.id}/edit`)}
        >
          <Pencil size={14} />
          Editar
        </Button>
      </div>

      <ExpiryBanner
        items={[
          {
            date: driver.cnhExpiry,
            label: 'CNH',
            detail: driver.cnhCategory.length > 0
              ? `Categoria ${driver.cnhCategory.join(', ')}`
              : undefined,
          },
        ]}
      />

      <div className="space-y-5 rounded-lg border border-border p-5">
        <div>
          <h2 className="mb-3 text-sm font-medium text-foreground">Dados pessoais</h2>
          <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <DetailRow label="CPF" value={maskCpf(driver.cpf)} />
            <DetailRow label="RG" value={driver.rg} />
            <DetailRow
              label="Telefone"
              value={driver.phone ? maskPhone(driver.phone) : null}
            />
            <DetailRow label="Email" value={driver.email} />
          </dl>
        </div>

        {hasCnh && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-3 text-sm font-medium text-foreground">CNH</h2>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <DetailRow label="Número" value={driver.cnhNumber} />
              <div>
                <dt className="text-xs font-medium text-muted-foreground">Categoria(s)</dt>
                <dd className="mt-0.5">
                  {driver.cnhCategory.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {driver.cnhCategory.map((cat) => (
                        <span
                          key={cat}
                          className="rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-foreground"
                        >
                          {cat}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-sm text-foreground">—</span>
                  )}
                </dd>
              </div>
              <DetailRow label="Validade" value={formatDate(driver.cnhExpiry)} />
            </dl>
          </div>
        )}

        {hasAddress && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-3 text-sm font-medium text-foreground">Endereço</h2>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <DetailRow label="CEP" value={driver.zipCode} />
              <div className="col-span-2">
                <DetailRow label="Logradouro" value={driver.street} />
              </div>
              <DetailRow label="Número" value={driver.number} />
              <DetailRow label="Complemento" value={driver.complement} />
              <DetailRow label="Bairro" value={driver.neighborhood} />
              <DetailRow label="Cidade" value={driver.city} />
              <DetailRow label="UF" value={driver.state} />
            </dl>
          </div>
        )}

        {hasBank && (
          <div className="border-t border-border pt-4">
            <h2 className="mb-3 text-sm font-medium text-foreground">Dados bancários</h2>
            <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <DetailRow label="Banco" value={driver.bankName} />
              <DetailRow
                label="Tipo de conta"
                value={driver.bankAccountType ? BANK_TYPE_LABEL[driver.bankAccountType] : null}
              />
              <DetailRow label="Agência" value={driver.bankAgency} />
              <DetailRow label="Conta" value={driver.bankAccount} />
              <div className="col-span-2">
                <DetailRow label="Chave Pix" value={driver.pixKey} />
              </div>
            </dl>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button
          variant={driver.active ? 'destructive' : 'default'}
          onClick={() => setConfirmOpen(true)}
          disabled={isPending}
        >
          {driver.active ? 'Desativar motorista' : 'Ativar motorista'}
        </Button>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={driver.active ? 'Desativar motorista?' : 'Ativar motorista?'}
        description={
          driver.active
            ? `O motorista "${driver.name}" ficará inativo e não aparecerá em novos fretes.`
            : `O motorista "${driver.name}" voltará a ficar disponível.`
        }
        confirmLabel={driver.active ? 'Desativar' : 'Ativar'}
        destructive={driver.active}
        loading={isPending}
        onConfirm={handleToggle}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  )
}
